import React, { createContext, useContext, useEffect, useState, useCallback, useRef, useMemo } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const CartContext = createContext(null);

const LOCAL_KEY = "sf_guest_cart";

const sameLine = (a, b) =>
  a.product_id === b.product_id && (a.size || null) === (b.size || null) && (a.color || null) === (b.color || null);

export const CartProvider = ({ children }) => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const lastUserIdRef = useRef(null);

  const reload = useCallback(async () => {
    if (user) {
      // Detect first-time login transition and merge any guest-cart items into the server cart
      // CRITICAL: set the ref BEFORE any awaits to prevent concurrent reload() calls from
      // double-merging (React StrictMode double-invoke + AuthProvider checkAuth race).
      if (lastUserIdRef.current !== user.user_id) {
        lastUserIdRef.current = user.user_id;
        const local = JSON.parse(localStorage.getItem(LOCAL_KEY) || "[]");
        if (local.length > 0) {
          // Clear localStorage immediately so a concurrent invocation can't read it again
          localStorage.removeItem(LOCAL_KEY);
          try {
            for (const it of local) {
              await api.post("/cart/add", { product_id: it.product_id, qty: it.qty, size: it.size, color: it.color || null });
            }
          } catch (e) { console.warn("Guest cart merge failed", e); }
        }
      }
      try {
        const res = await api.get("/cart");
        setItems(res.data.items || []);
      } catch {
        setItems([]);
      }
    } else {
      lastUserIdRef.current = null;
      const local = JSON.parse(localStorage.getItem(LOCAL_KEY) || "[]");
      setItems(local);
    }
  }, [user]);

  useEffect(() => { reload(); }, [reload]);

  const addItem = async (product, qty = 1, size = null, color = null) => {
    if (user) {
      await api.post("/cart/add", { product_id: product.product_id, qty, size, color });
      await reload();
    } else {
      const local = JSON.parse(localStorage.getItem(LOCAL_KEY) || "[]");
      const newLine = { product_id: product.product_id, qty, size, color };
      const idx = local.findIndex((i) => sameLine(i, newLine));
      if (idx >= 0) local[idx].qty += qty;
      else local.push({ ...newLine, product });
      localStorage.setItem(LOCAL_KEY, JSON.stringify(local));
      setItems(local);
    }
    toast.success(`${product.name} added to cart`);
    setDrawerOpen(true);
  };

  const updateQty = async (product_id, size, color, qty) => {
    if (user) {
      await api.post("/cart/update", { product_id, qty, size, color });
      await reload();
    } else {
      let local = JSON.parse(localStorage.getItem(LOCAL_KEY) || "[]");
      local = local.map((i) =>
        sameLine(i, { product_id, size, color }) ? { ...i, qty } : i
      ).filter((i) => i.qty > 0);
      localStorage.setItem(LOCAL_KEY, JSON.stringify(local));
      setItems(local);
    }
  };

  const removeItem = async (product_id, size, color) => {
    if (user) {
      await api.post("/cart/remove", { product_id, qty: 0, size, color });
      await reload();
    } else {
      const local = JSON.parse(localStorage.getItem(LOCAL_KEY) || "[]")
        .filter((i) => !sameLine(i, { product_id, size, color }));
      localStorage.setItem(LOCAL_KEY, JSON.stringify(local));
      setItems(local);
    }
  };

  const clear = async () => {
    if (user) await api.post("/cart/clear");
    localStorage.removeItem(LOCAL_KEY);
    setItems([]);
  };

  const count = items.reduce((s, i) => s + (i.qty || 0), 0);
  const subtotal = items.reduce((s, i) => s + ((i.product?.price || 0) * (i.qty || 0)), 0);

  const value = useMemo(
    () => ({ items, count, subtotal, addItem, updateQty, removeItem, clear, drawerOpen, setDrawerOpen, reload }),
    [items, count, subtotal, drawerOpen, reload] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
